import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { Toast as BSToast, ToastContainer } from 'react-bootstrap'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastMessage {
    id: string
    type: ToastType
    title: string
    message: string
    duration?: number
}

interface ToastContextType {
    showToast: (type: ToastType, title: string, message: string, duration?: number) => void
    success: (title: string, message: string, duration?: number) => void
    error: (title: string, message: string, duration?: number) => void
    warning: (title: string, message: string, duration?: number) => void
    info: (title: string, message: string, duration?: number) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToast = () => {
    const context = useContext(ToastContext)
    if (!context) {
        // Provide a fallback for development/HMR scenarios
        console.warn('useToast called outside ToastProvider, using fallback')
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return {
            showToast: () => { /* no-op fallback */ },
            success: () => { /* no-op fallback */ },
            error: () => { /* no-op fallback */ },
            warning: () => { /* no-op fallback */ },
            info: () => { /* no-op fallback */ }
        }
    }
    return context
}

interface ToastProviderProps {
    children: ReactNode
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastMessage[]>([])

    const showToast = useCallback((
        type: ToastType,
        title: string,
        message: string,
        duration = 5000
    ) => {
        const id = `toast-${Date.now()}-${Math.random()}`
        const newToast: ToastMessage = { id, type, title, message, duration }

        setToasts(prev => [...prev, newToast])

        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id))
        }, duration)
    }, [])

    const success = useCallback((title: string, message: string, duration?: number) => {
        showToast('success', title, message, duration)
    }, [showToast])

    const error = useCallback((title: string, message: string, duration?: number) => {
        showToast('error', title, message, duration)
    }, [showToast])

    const warning = useCallback((title: string, message: string, duration?: number) => {
        showToast('warning', title, message, duration)
    }, [showToast])

    const info = useCallback((title: string, message: string, duration?: number) => {
        showToast('info', title, message, duration)
    }, [showToast])

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id))
    }, [])

    const getIcon = (type: ToastType) => {
        switch (type) {
            case 'success': return 'fa-check-circle'
            case 'error': return 'fa-exclamation-circle'
            case 'warning': return 'fa-exclamation-triangle'
            case 'info': return 'fa-info-circle'
        }
    }

    const getBg = (type: ToastType) => {
        switch (type) {
            case 'success': return 'success'
            case 'error': return 'danger'
            case 'warning': return 'warning'
            case 'info': return 'info'
        }
    }

    return (
        <ToastContext.Provider value={{ showToast, success, error, warning, info }}>
            {children}
            <ToastContainer
                position="top-end"
                className="p-3"
                style={{ zIndex: 9999 }}
            >
                {toasts.map(toast => (
                    <BSToast
                        key={toast.id}
                        onClose={() => removeToast(toast.id)}
                        show={true}
                        delay={toast.duration}
                        autohide
                        bg={getBg(toast.type)}
                    >
                        <BSToast.Header>
                            <i className={`fa ${getIcon(toast.type)} me-2`}></i>
                            <strong className="me-auto">{toast.title}</strong>
                        </BSToast.Header>
                        <BSToast.Body className={toast.type === 'warning' || toast.type === 'info' ? 'text-dark' : 'text-white'}>
                            {toast.message}
                        </BSToast.Body>
                    </BSToast>
                ))}
            </ToastContainer>
        </ToastContext.Provider>
    )
}
